import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { PAIRS } from "@/lib/languages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-border/60"
    >
      <div className="container-x flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2 group"
        >
          <img src="/logo.png" alt="Tiny Stem" className="h-9 w-9 rounded-md" width="36" height="36" loading="eager" fetchPriority="high" />
          <span className="font-display font-black text-xl tracking-tighter">
            Tiny<span className="text-primary"> Stem</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  isActive ? "text-primary" : "text-foreground hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-languages-trigger"
              className="flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors outline-none"
            >
              Languages
              <ChevronDown className="h-4 w-4" strokeWidth={1.75} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {PAIRS.map((p) => (
                <DropdownMenuItem key={p.slug} asChild>
                  <Link
                    to={`/${p.slug}`}
                    data-testid={`nav-lang-${p.slug}`}
                    className="cursor-pointer w-full"
                  >
                    {p.keyword}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-border"
                aria-label="Open menu"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle className="font-display tracking-tighter">
                  Menu
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-4">
                {primaryLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="text-base font-medium py-2"
                  >
                    {l.label}
                  </Link>
                ))}
                <div className="pt-4 border-t border-border">
                  <p className="overline mb-3">Language pairs</p>
                  <div className="flex flex-col gap-2">
                    {PAIRS.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/${p.slug}`}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-lang-${p.slug}`}
                        className="text-sm text-muted-foreground hover:text-primary"
                      >
                        {p.keyword}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
